import { BCMSCurrency, BCMSDate, BCMSRecord, BCMSRecordProperty } from "@bcm-dev/bcms-vite";
import { Account } from "model";

/**
 * @class Autopay
 * @classdesc Class definition for the recod type from model
 * @param {OBJECT} record - The record from the model to init class
 * @extends BCMSRecord
 * @example const record = new Autopay(model.autopays[0]);
 * 
 * @property {STRING} _id - The id of the autopay
 * @property {BCMSDate|String} _deliverBy - The delivery by date, it can either be an offset or instance of BCMSDate
 * @property {Account} _payFrom - The account that the amount should be paid from
 * @property {BCMSCurrency|String} _alwaysPay - The amount that should always be paid, it can either be an offset or instance of BCMSCurrency
 * @property {STRING} _frequency - The frequency of the autopay
 * @property {STRING} _duration - The duration of the autopay
 * @property {STRING} _memo - The memo of the autopay
 * @property {BCMSCurrency} _totalAmount - The total amount of the autopay
 * @property {STRING} _billerID - The id of the associated biller
 * @property {BCMSCurrency} _maximum - The maxium amount of the autopay
 * @property {BCMSCurrency} _fixed - The fixed amount to pay for the autopay
 * @property {BCMSDate} _finalDeliveryBy - The finalDeliveryBy date for the autopay.
 * @property {STRING} _numberOfPayments - The numberOfPayments for the autopay.
 * @property {STRING} _lastPaymentAmount - The lastPaymentAmount when using a duration for the autopay.
 * @property {BOOLEAN} _notificationScheduled - The notificationScheduled status for the autopay.
 * @property {BOOLEAN} _notificationSent - The notificationSent status for the autopay.
 * @property {BOOLEAN} _notificationSent - The notificationLastOne status for the autopay.
 */
export default class Autopay extends BCMSRecord {
    _id;
    _deliverBy;
    _payFrom;
    _alwaysPay;
    _frequency;
    _duration;
    _memo;
    _totalAmount;
    _billerID;
    _maximum;
    _fixed;
    _finalDeliveryBy;
    _numberOfPayments;
    _lastPaymentAmount;
    _notificationScheduled;
    _notificationSent;
    _notificationLastOne;
    
    constructor(record) {
        super(record);
    }

    /**
     * @function formatDate
     * @instance
     * @returns {STRING}
     * @description Returns a formatted date string based on if the year is the same as the current or not
     * @memberof Autopay
     */
    formatDate(property) {
        let currentYear = BCMSDate.init().yearInt;
        let selectedProperty = property === undefined ? "deliverBy" : property;

        var dateFormatted = this[selectedProperty].format('M d');

        if(currentYear !== this[selectedProperty].yearInt) {
            dateFormatted = this[selectedProperty].format('M d, Y');
        }

        return dateFormatted;
    }

    /**
     * @function getByBillerID
     * @param {STRING} id - The id of the biller
     * @returns {ARRAY}
     * @static
     * @description Return all the autopays by the id of the biller associated
     * @memberof Autopay
     */
    static getByBillerID(id) {
        return Autopay.initAll().filter(item => item.billerID === id, []);
    }

    static getAutopaySuccessMessage(routeData) {
        let successMessage = "";
        // Check if an autopay has recently been deleted in order to show a message for that action.
        // Instances of "autopay" and "biller" as passed through a route, but their class types are removed
        // and are therefore plain js objects here.
        const autopay = routeData.autopay;
        const biller = routeData.biller;
        const alwaysPay = autopay._alwaysPay;
        
        if(alwaysPay === "Amount due") {
            successMessage = `Your autopay plan for ${ biller._name } ${ biller._number } has been deleted.`;
        } else if(alwaysPay === "Fixed amount") {
            successMessage = `Your ${ BCMSCurrency.init(autopay._fixed._amount).format() } autopay plan for ${ biller._name } ${ biller._number } has been deleted.`;
        } else {
            successMessage = `Your ${ BCMSCurrency.init(alwaysPay._amount).format() } autopay plan for ${ biller._name } ${ biller._number } has been deleted.`;
        }
        
        return successMessage;
    }
}

Autopay.define({
    "recordType" : "autopays",
    "recordIDProp" : "autopayID",
    "className" : "Autopay",
    "recordProperties" : [
        /**
         * @name id
         * @instance
         * @property get {STRING} - Gets the id of the autopay
         * @property set {STRING} - Sets the id of the autopay
         * @memberof Autopay
         */
        BCMSRecordProperty.mapToString("id", "_id", "autopayID"),
        
        /**
         * @name deliverBy
         * @instance
         * @property get {BCMSDate|STRING} - Gets the deliverBy of the autopay
         * @property set {BCMSDate|STRING} - Sets the deliverBy of the autopay
         * @memberof Autopay
         */
        BCMSRecordProperty.mapTo({
            // Defining how the deliverBy property should work
            // NOTE: Defining the long way since the value can either be a date or a string
            
            "id" : "deliverBy",
            "instanceField" : "_deliverBy",
            "modelPropID" : "autopayDeliverBy",
            "setter" : function(value) {
                // If the value is already an instance of BCMSDate OR is not a number set the value as it
                // Otherwise initalize a new BCMSDate instance
                this._deliverBy = (value instanceof BCMSDate || isNaN(value))
                    ? value
                    : BCMSDate.init().offset(value);
            },
            "toRecord" : function() {
                return this._deliverBy instanceof BCMSDate ? this._deliverBy.diff() : this._deliverBy;
            }
        }),

        /**
         * @name payFrom
         * @instance
         * @property get {Account} - Gets the payFrom account of the autopay
         * @property set {Account|STRING} - Sets the payFrom account of the autopay
         * @memberof Autopay
         */
        BCMSRecordProperty.mapToClass("payFrom", "_payFrom", () => Account, "autopayAccount"),

        /**
         * @name alwaysPay
         * @instance
         * @property get {BCMSCurrency|STRING} - Gets the alwaysPay property of the autopay
         * @property set {BCMSCurrency|STRING} - Sets the alwaysPay property of the autopay
         * @memberof Autopay
         */
        BCMSRecordProperty.mapTo({ 
            // Defining how the alwaysPay property should work
            // NOTE: Defining the long way since the value can either by a currency or a string

            "id" : "alwaysPay", 
            "instanceField" : "_alwaysPay", 
            "modelPropID" : "autopayAlwaysPay",
            "setter" : function(value) {
                // If the value is a number, than initalize a new BCMSCurrency instance otherwise set the value as is
                this._alwaysPay = !isNaN(value) ? BCMSCurrency.init(value) : value;
            },
            "toRecord": function() {
                return this._alwaysPay instanceof BCMSCurrency ? this._alwaysPay.amount : this._alwaysPay;
            }
        }),

        /**
         * @name frequency
         * @instance
         * @property get {STRING} - Gets the frequency of the autopay
         * @property set {STRING} - Sets the frequency of the autopay
         * @memberof Autopay
         */
        BCMSRecordProperty.mapToString("frequency", "_frequency", "autopayFrequency"),

        /**
         * @name duration
         * @instance
         * @property get {STRING} - Gets the duration of the autopay
         * @property set {STRING} - Sets the duration of the autopay
         * @memberof Autopay
         */
        BCMSRecordProperty.mapToString("duration", "_duration", "autopayDuration"),

        /**
         * @name memo
         * @instance
         * @property get {STRING} - Gets the memo of the autopay
         * @property set {STRING} - Sets the memo of the autopay
         * @memberof Autopay
         */
        BCMSRecordProperty.mapToString("memo", "_memo", "autopayMemo"),

        /**
         * @name totalAmount
         * @instance
         * @property get {BCMSCurrency} - Gets the totalAmount of the autopay
         * @property set {BCMSCurrency} - Sets the totalAmount of the autopay
         * @memberof Autopay
         */
        BCMSRecordProperty.mapToCurrency("totalAmount", "_totalAmount", "autopayTotalAmount"),
        
        // Added the biller id to the autopay so we can technically show multiple autopays for a single biller
        // NOTE: Do NOT map to the biller class, will throw a maximum call stack since a biller has an autopay
        // and an autopay has a biller

        /**
         * @name billerID
         * @instance
         * @property get {STRING} - Gets the billerID of the autopay
         * @property set {STRING} - Sets the billerID of the auotpay
         * @memberof Autopay
         */
        BCMSRecordProperty.mapToString("billerID", "_billerID", "autopayBiller"),

        // Added these fields for the short form autopay

        /**
         * @name maximum
         * @instance
         * @property get {BCMSCurrency} - Gets the maximum amount of the autopay
         * @property set {BCMSCurrency} - Sets the maximum amount of the autopay
         * @memberof Autopay
         */
        BCMSRecordProperty.mapToCurrency("maximum", "_maximum", "autopayMaximum"),

        /**
         * @name fixed
         * @instance
         * @property get {BCMSCurrency} - Gets the fixed amount of the autopay
         * @property set {BCMSCurrency} - Sets the fixed amount of the autopay
         * @memberof Autopay
         */
        BCMSRecordProperty.mapToCurrency("fixed", "_fixed", "autopayFixed"),

        /**
         * @name finalDeliveryBy
         * @instance
         * @property get {BCMSDate} - gets the finalDeliveryBy of the autopay
         * @property set {BCMSDate} - sets the finalDeliveryBy of the autopay
         * @memberof Autopay
         */
        BCMSRecordProperty.mapToDate("finalDeliveryBy", "_finalDeliveryBy", "autopayFinalDeliveryBy"),

        /**
         * @name numberOfPayments
         * @instance
         * @property get {BCMSCurrency} - gets the numberOfPayments of the autopay
         * @property set {BCMSCurrency} - sets the numberOfPayments of the autopay
         * @memberof Autopay
         */
        BCMSRecordProperty.mapToString("numberOfPayments", "_numberOfPayments", "autopayNumberOfPayments"),

        /**
         * @name lastPaymentAmount
         * @instance
         * @property get {BCMSCurrency} - gets the lastPaymentAmount of the autopay
         * @property set {BCMSCurrency} - sets the lastPaymentAmount of the autopay
         * @memberof Autopay
         */
        BCMSRecordProperty.mapToCurrency("lastPaymentAmount", "_lastPaymentAmount", "autopayLastPaymentAmount"),

        /**
         * @name notificationScheduled
         * @instance
         * @property get {BOOLEAN} - gets the notificationScheduled of the autopay
         * @property set {BOOLEAN} - sets the notificationScheduled of the autopay
         * @memberof Autopay
         */
        BCMSRecordProperty.mapToBoolean("notificationScheduled", "_notificationScheduled", "autopayNotificationScheduled"),

        /**
         * @name notificationSent
         * @instance
         * @property get {BOOLEAN} - gets the notificationSent of the autopay
         * @property set {BOOLEAN} - sets the notificationSent of the autopay
         * @memberof Autopay
         */
        BCMSRecordProperty.mapToBoolean("notificationSent", "_notificationSent", "autopayNotificationSent"),

        /**
         * @name notificationLastOne
         * @instance
         * @property get {BOOLEAN} - gets the notificationLastOne of the autopay
         * @property set {BOOLEAN} - sets the notificationLastOne of the autopay
         * @memberof Autopay
         */
        BCMSRecordProperty.mapToBoolean("notificationLastOne", "_notificationLastOne", "autopayNotificationLastOne")
    ]
});
